import { useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent as ReactFocusEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Link } from 'react-router';
import { Icon } from './icon';

// Material's plain tooltip (TF-96), in place of the browser's `title`, which never shows on a touch
// screen or on keyboard focus. One layer for the whole app: any element with `data-tooltip` - and
// every icon-only button, from its `aria-label` - shows its hint on hover after 500 ms, at once on
// keyboard focus, and on a long press. The hint is tied to the control with aria-describedby while
// it shows, unless it only repeats the control's name.

const TOOLTIP_ID = 'tf-tooltip';
const HOVER_MS = 500;
const LONG_PRESS_MS = 500;
const TOUCH_SHOWN_MS = 1500;
const SELECTOR = '[data-tooltip]:not([data-tooltip=""]), .icon-btn[aria-label]';

interface Shown {
  text: string;
  top: number;
  /** The middle of the control: the hint is centred on it when that fits in the window. */
  center: number;
  below: boolean;
}

/** Kept this far inside the window's edges (TF-317). */
const EDGE = 8;

/**
 * Where a hint of `width` starts: centred on `center`, moved in to stay `EDGE` inside a window
 * `viewport` wide (TF-317). A hint wider than the window starts at the edge.
 */
export function tooltipLeft(center: number, width: number, viewport: number): number {
  return Math.max(EDGE, Math.min(center - width / 2, viewport - EDGE - width));
}

function hintOf(element: Element): string | null {
  return element.getAttribute('data-tooltip') || element.getAttribute('aria-label');
}

export function TooltipLayer() {
  const [shown, setShown] = useState<Shown | null>(null);
  const target = useRef<Element | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const describedBefore = useRef<string | null>(null);

  useEffect(() => {
    const clear = () => window.clearTimeout(timer.current);
    const hide = () => {
      clear();
      const element = target.current;
      if (element && element.getAttribute('aria-describedby')?.includes(TOOLTIP_ID)) {
        if (describedBefore.current) element.setAttribute('aria-describedby', describedBefore.current);
        else element.removeAttribute('aria-describedby');
      }
      target.current = null;
      setShown(null);
    };
    const show = (element: Element) => {
      const text = hintOf(element);
      if (!text || !element.isConnected) return;
      target.current = element;
      const box = element.getBoundingClientRect();
      // Above the control, or below it when there is no room above.
      const below = box.top < 44;
      setShown({ text, center: box.left + box.width / 2, top: below ? box.bottom + 8 : box.top - 8, below });
      // Described by the hint only when it says more than the control's own name.
      if (text !== element.getAttribute('aria-label')) {
        describedBefore.current = element.getAttribute('aria-describedby');
        element.setAttribute('aria-describedby', [describedBefore.current, TOOLTIP_ID].filter(Boolean).join(' '));
      }
    };
    const elementFrom = (node: EventTarget | null) => (node instanceof Element ? node.closest(SELECTOR) : null);

    const over = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const element = elementFrom(event.target);
      if (!element || element === target.current) return;
      hide();
      timer.current = window.setTimeout(() => show(element), HOVER_MS);
    };
    const out = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const element = elementFrom(event.target);
      if (element && event.relatedTarget instanceof Node && element.contains(event.relatedTarget)) return;
      hide();
    };
    const focusIn = (event: FocusEvent) => {
      const element = elementFrom(event.target);
      if (!element || !(event.target as Element).matches(':focus-visible')) return;
      hide();
      show(element);
    };
    const down = (event: PointerEvent) => {
      hide();
      if (event.pointerType !== 'touch') return;
      const element = elementFrom(event.target);
      if (!element) return;
      timer.current = window.setTimeout(() => {
        show(element);
        timer.current = window.setTimeout(hide, TOUCH_SHOWN_MS + LONG_PRESS_MS);
      }, LONG_PRESS_MS);
    };
    const cancelPress = (event: PointerEvent) => {
      if (event.pointerType === 'touch' && !target.current) clear();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };

    document.addEventListener('pointerover', over);
    document.addEventListener('pointerout', out);
    document.addEventListener('focusin', focusIn);
    document.addEventListener('focusout', hide);
    document.addEventListener('pointerdown', down);
    document.addEventListener('pointerup', cancelPress);
    document.addEventListener('pointercancel', cancelPress);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', hide, true);
    return () => {
      clear();
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerout', out);
      document.removeEventListener('focusin', focusIn);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('pointerup', cancelPress);
      document.removeEventListener('pointercancel', cancelPress);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  // Measured at its own width before the browser paints, then placed inside the window (TF-317): near
  // an edge it moves in rather than squeezing into a thin column.
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = box.current;
    if (!shown || !element) return;
    element.style.left = `${tooltipLeft(shown.center, element.offsetWidth, document.documentElement.clientWidth)}px`;
  }, [shown]);

  return (
    <div
      ref={box}
      id={TOOLTIP_ID}
      role="tooltip"
      className={shown ? `tooltip${shown.below ? ' tooltip-below' : ''}` : 'tooltip'}
      hidden={!shown}
      style={shown ? { top: shown.top, left: 0 } : undefined}
    >
      {shown?.text}
    </div>
  );
}

/**
 * Material's rich tooltip (TF-96): a title, a sentence or two and perhaps an action, for what needs
 * explaining - a plan's limits. Opened by its info button (tap, click or Enter), closed by Escape or
 * a tap elsewhere.
 */
export function RichTooltip({ label, title, children, action }: { label: string; title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <Popover.Root>
      <Popover.Trigger className="icon-btn rich-tooltip-trigger" aria-label={label} data-tooltip="">
        <Icon name="info" size={20} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="rich-tooltip" side="top" align="start" sideOffset={6} collisionPadding={16}>
          <strong className="rich-tooltip-title">{title}</strong>
          <div className="rich-tooltip-text">{children}</div>
          {action && <div className="rich-tooltip-action">{action}</div>}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const HOVER_CARD_MS = 300;

/**
 * A rich tooltip that opens by pointing (TF-80): after 300 ms of a mouse over its control, at once on
 * keyboard focus, and on a tap or click, which also takes the focus into it so its links can be
 * reached. It stays open while the pointer is over it; leaving, Escape or a tap elsewhere closes it.
 * The control describes itself for screen readers with `description`, since a popup that opens on
 * focus is not announced. Given `to`, the control is a link (a chart's band, TF-135): pointing and
 * keyboard focus show the card, a click follows the link.
 */
export function HoverCard({
  children,
  content,
  description,
  className,
  label,
  to,
}: {
  /** The control's own content: it is a button, or a link with `to`. */
  children: ReactNode;
  content: ReactNode;
  description: string;
  className?: string;
  label?: string;
  to?: string;
}) {
  const [open, setOpen] = useState<'hover' | 'focus' | 'click' | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const trigger = useRef<HTMLButtonElement & HTMLAnchorElement>(null);
  const describedBy = useId();
  const later = (next: 'hover' | null, ms: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen((current) => (next === null && current === 'click' ? current : next)), ms);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const shared = {
    ref: trigger,
    className,
    'aria-label': label,
    'aria-describedby': describedBy,
    onPointerEnter: (event: ReactPointerEvent) => event.pointerType === 'mouse' && open === null && later('hover', HOVER_CARD_MS),
    onPointerLeave: (event: ReactPointerEvent) => event.pointerType === 'mouse' && later(null, 150),
    onFocus: (event: ReactFocusEvent<HTMLElement>) => {
      if (!event.currentTarget.matches(':focus-visible')) return;
      // Back before a close it had set off: it stays.
      clearTimeout(timer.current);
      setOpen((current) => current ?? 'focus');
    },
    onBlur: () => open === 'focus' && later(null, 100),
  };
  const said = (
    <span id={describedBy} className="visually-hidden">
      {description}
    </span>
  );
  const control = to ? (
    <Link to={to} {...shared}>
      {children}
      {said}
    </Link>
  ) : (
    <button
      type="button"
      aria-expanded={open !== null}
      {...shared}
      onClick={() => {
        clearTimeout(timer.current);
        setOpen((current) => (current === 'click' ? null : 'click'));
      }}
    >
      {children}
      {said}
    </button>
  );
  return (
    <Popover.Root open={open !== null} onOpenChange={(next) => !next && setOpen(null)}>
      <Popover.Anchor asChild>
        {control}
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          className="rich-tooltip hover-card"
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={16}
          // Pointing or keyboard focus only shows it; a click or tap goes into it.
          onOpenAutoFocus={(event) => open !== 'click' && event.preventDefault()}
          onCloseAutoFocus={(event) => open !== 'click' && event.preventDefault()}
          // The control itself toggles it, rather than counting as a tap elsewhere.
          onPointerDownOutside={(event) => trigger.current?.contains(event.target as Node) && event.preventDefault()}
          onFocusOutside={(event) => trigger.current?.contains(event.target as Node) && event.preventDefault()}
          onPointerEnter={() => clearTimeout(timer.current)}
          onPointerLeave={(event) => event.pointerType === 'mouse' && later(null, 150)}
        >
          {content}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

